import { WorkflowAction } from './WorkflowAction';

// The status the integration reports on sex_offender_screen_result:
//   - 'match' — offenders.io returned candidate records; the action settles `completed` and
//     the API's #effective_status folds it to `flagged` for the referral UI to attend (mirrors
//     DenialCheck). Rule C: the flag burns off once every candidate is dismissed.
//   - 'clear' — no matches; the action completes green.
//   - 'error' — offenders.io failed; the action errored.
export const SEX_OFFENDER_RESULT = { MATCH: 'match', CLEAR: 'clear', ERROR: 'error' } as const;

// Per-match adjudication decisions the FE writes to sex_offender_match_reviews. Every click
// (first or Nth, Match or Dismiss) is a save_progress write to action.outputs — no lifecycle
// transition, since the integration always settles the action to `completed` on a terminal
// result. The FE reads them back off action.outputs (never a workflow value).
export const REVIEW_DECISION = { MATCH: 'match', DISMISS: 'dismiss' } as const;
export type ReviewDecision = (typeof REVIEW_DECISION)[keyof typeof REVIEW_DECISION];

export interface MatchReview {
  // The offenders.io UUIDv5 of the match this decision applies to. Kept as a VALUE (not a
  // hash key) so OliveBranch's kebab/snake/camel transforms don't mangle it — UUIDs contain
  // dashes and would round-trip differently on each request (see OLIO-2391 comment history).
  uuid: string;
  decision: ReviewDecision;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

// Reviews are an array on the wire and internally — one entry per adjudicated match. Callers
// look up by uuid via `reviewFor()` on the action model rather than array-indexing.
export type MatchReviews = MatchReview[];

// Structured per-crime entry from offenders.io extensive mode (stateData.offenses[]). Field names
// mirror the API shape verbatim (offense / convictionDate / jurisdiction) — earlier renames to
// description/date/location silently produced empty crime bullets. The `offense` string can itself
// contain embedded `\n` characters for records the aggregator couldn't normalize (VA in
// particular), so render it with `white-space: pre-line`. All fields are per-registry optional —
// Arizona often returns `offense` alone, some sources add `statute`/`caseNumber`/`releaseDate`.
export interface OffenderOffense {
  offense?: string | null;
  convictionDate?: string | null;
  jurisdiction?: string | null;
  statute?: string | null;
  caseNumber?: string | null;
  releaseDate?: string | null;
  [key: string]: unknown;
}

export interface OffenderStateData {
  status?: string | null; // "Active" / etc. — badge on the match card
  designation?: string | null; // e.g. "Level 1 Predator" — badge on the match card
  offenses?: OffenderOffense[];
  [key: string]: unknown;
}

export interface OffenderSource {
  id?: string;
  name?: string | null;
  url?: string | null;
}

// A single sex-offender registry candidate as it comes back from the integration platform
// (offenders.io response passed through verbatim). All fields are optional — the mockup renders
// "–" for absent values.
export interface OffenderMatch {
  uuid: string; // UUIDv5 pass-through — the stable key for adjudication (OLIO-2391)
  name?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  dob?: string | null;
  age?: string | null;
  sex?: string | null;
  race?: string | null;
  ethnicity?: string | null;
  hairColor?: string | null;
  eyeColor?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  crime?: string | null;
  riskLevel?: string | null;
  isPredator?: boolean | null;
  isAbsconder?: boolean | null;
  offenderUrl?: string | null;
  offenderImageUrl?: string | null;
  offenderImageUrlR2?: string | null;
  sources?: OffenderSource[];
  stateData?: OffenderStateData | null;
  // Client-side scoring fields computed at driver time by the integration's scoring module
  // (see olio-integration app/apis/offendercheck/scoring.py + OFFENDERS_IO_NOTES §5). Frozen
  // into the result body at review time so both consumer surfaces render identical adjudication
  // signals and historical checks stay locked to the weights that existed then.
  //
  // Rails camelCases the wire fields via OliveBranch (snake_case in Python → camelCase here).
  nameTier?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | null;
  nameScore?: number | null;
  dobDeltaYears?: number | null;
  dobScore?: number | null;
  matchScore?: number | null;
  matchBucket?: 'strong' | 'plausible' | 'unlikely' | 'very_unlikely' | null;
  [key: string]: unknown;
}

interface ScreenResult {
  status?: string;
  matches?: OffenderMatch[];
  error?: string;
}

// The SexOffenderCheck action kind. offenders.io returns candidate matches when demographics
// hit a registry; the integration always settles the action to `completed` on a terminal
// result (matches or clear). A person adjudicates each candidate Dismiss/Match in the
// slideout; every decision is a save_progress write to `action.outputs['sex_offender_match_reviews']`
// via saveWorkflowActionProgress — no lifecycle transition, no first-vs-subsequent distinction.
//
// Presentation folds server-side (SexOffenderCheck#effective_status): `completed + match →
// flagged`, burning off to `completed` when every candidate is dismissed (Rule C). This model
// exposes only the data the FE renders.
export class SexOffenderCheckAction extends WorkflowAction {
  // The screen result rides on action.outputs (echoed by the FE alongside reviews on every
  // save_progress write) and on the workflow value the integration callback published. In-model
  // we only see action.outputs; workflow-value fallback isn't needed because the FE echoes it.
  private get result(): ScreenResult {
    const value = this.outputFor('sex_offender_screen_result');
    return value && typeof value === 'object' ? (value as ScreenResult) : {};
  }

  get screenResult(): ScreenResult {
    return this.result;
  }

  get determination(): string | undefined {
    return this.result.status;
  }

  get isMatch(): boolean {
    return this.determination === SEX_OFFENDER_RESULT.MATCH;
  }

  get matches(): OffenderMatch[] {
    const matches = this.result.matches;
    return Array.isArray(matches) ? matches : [];
  }

  // Per-match reviews recorded so far (empty until the first Dismiss / Match click). An array
  // of `{uuid, decision, ...}` — the FE overlays it onto matches[] to render per-card state.
  // Wire shape is intentionally array (see MatchReview docstring).
  get reviews(): MatchReviews {
    const value = this.outputFor('sex_offender_match_reviews');
    return Array.isArray(value) ? (value as MatchReviews) : [];
  }

  // Whether any per-match decision has been recorded — signals the caller to use
  // saveWorkflowActionProgress (patch-only) instead of completeWorkflowAction (transition).
  get hasAnyReview(): boolean {
    return this.reviews.length > 0;
  }

  reviewFor(uuid: string): MatchReview | undefined {
    return this.reviews.find((r) => r.uuid === uuid);
  }

  // Matches the FE renders as active (default state). Dismissed cards move behind the
  // "View Dismissed Result" affordance; matched cards render in a distinct highlighted style.
  get activeMatches(): OffenderMatch[] {
    return this.matches.filter((m) => this.reviewFor(m.uuid)?.decision !== REVIEW_DECISION.DISMISS);
  }

  get dismissedMatches(): OffenderMatch[] {
    return this.matches.filter((m) => this.reviewFor(m.uuid)?.decision === REVIEW_DECISION.DISMISS);
  }

  get matchedMatches(): OffenderMatch[] {
    return this.matches.filter((m) => this.reviewFor(m.uuid)?.decision === REVIEW_DECISION.MATCH);
  }

  // The patient name the check ran against — pulled from this instance's input snapshot so the
  // slideout heading reflects who was actually checked, even if workflow demographics later
  // change (OLIO-2391).
  get checkedName(): string {
    const value = (name: string) => this.inputs.find((i) => i.name === name)?.value ?? '';
    return `${value('patient_first_name')} ${value('patient_last_name')}`.trim();
  }
}
